import { Table, TableState, defaultTableState, tableStateSuffix } from "@components/Table"
import { RunGroup } from "@custom-types/modelRun"
import { getRunGroupTableColumns } from "@features/scenarioDesign/RunGroupTable/helpers"
import RunTable, { RunRow } from "@features/scenarioDesign/RunTable"
import { Box, Button, TableCellProps, Tooltip } from "@mui/material"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { useSessionStorage } from "usehooks-ts"
import { useAtomValue } from "jotai"
import { loggedInUserAtom } from "@store/authAtoms"
import RequestPublishingButton from "@features/scenarioDesign/RunGroupTable/RequestPublishingButton"
import { usePublishMajorGraphVersionMutation } from "@store/services/approvalsService"
import { enqueueSnackbar } from "notistack"
import logger from "@utils/logger"

export type RunGroupTableProps = {
  runGroups: RunGroup[]
  sessionStorageName: string
  runGroupToHighlightKey?: string
}

const RunGroupTable = ({
  runGroups,
  sessionStorageName,
  runGroupToHighlightKey,
}: RunGroupTableProps) => {
  const [publishGraphVersionMutation] = usePublishMajorGraphVersionMutation()
  const [selectedRunsKeys, setSelectedRunsKeys] = useState<RunRow["run_key"][]>([])
  const rows = runGroups
  const columns = getRunGroupTableColumns()
  const user = useAtomValue(loggedInUserAtom)
  const navigate = useNavigate()
  const [tableState, setTableState] = useSessionStorage<TableState>(
    `${sessionStorageName}${tableStateSuffix}`,
    defaultTableState,
  )

  useEffect(() => {
    const runGroupKeyToExpand = runGroupToHighlightKey
      ? rows.find((row) => row.key === runGroupToHighlightKey)
      : undefined

    if (!runGroupKeyToExpand) {
      return
    }

    setTableState((previousState) => ({
      ...previousState,
      expanded: {
        ...(typeof previousState.expanded === "object" ? previousState.expanded : {}),
        [runGroupKeyToExpand.key]: true,
      },
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [rows, runGroupToHighlightKey])

  const handleSelectionChange = (selectedRowKeys: string[], deselectedRowKeys: string[]) => {
    if (selectedRowKeys.length > 0) {
      setSelectedRunsKeys([...selectedRunsKeys, ...selectedRowKeys])
    }
    if (deselectedRowKeys.length > 0) {
      setSelectedRunsKeys(selectedRunsKeys.filter((key) => !deselectedRowKeys.includes(key)))
    }
  }

  const handleCompare = () => {
    const urlParams = new URLSearchParams({})
    selectedRunsKeys.forEach((runKey) => urlParams.append("runKeys", runKey))

    const firstSelectedRunGroup = runGroups.find((runGroup) =>
      Object.keys(runGroup.runs).includes(selectedRunsKeys[0] ?? ""),
    )
    if (!firstSelectedRunGroup) return
    urlParams.append("graphKey", firstSelectedRunGroup.base_graph_key)

    navigate(`/Results/Comparison?${urlParams.toString()}`)
  }

  const handleCollapseAll = () => {
    setTableState((previousState) => ({
      ...previousState,
      expanded: {},
    }))
  }

  const handleRequestPublishing = async (runGroup: RunGroup) => {
    try {
      await publishGraphVersionMutation({
        graphKey: runGroup.base_graph_key,
        runGroupKey: runGroup.key,
      }).unwrap()
      enqueueSnackbar("Graph publishing request has been created")
      // dispatch(runApi.util.invalidateTags(["Runs"]))
    } catch (e) {
      enqueueSnackbar("Failed to submit approval request", { variant: "error" })
      logger.error("Graph publishing ERROR: ", e)
    }
  }

  const isCompareButtonDisabled = selectedRunsKeys.length < 2 || selectedRunsKeys.length > 10

  return (
    <Table
      sessionStorageName={sessionStorageName}
      data={rows}
      columns={columns}
      enableExpandAll={false}
      getRowId={(runGroup) => runGroup.key}
      renderDetailPanel={({ row }) => {
        const runs = Object.entries(row.original.runs).map(([runKey, run]) => ({
          run_key: runKey,
          ...run,
        }))
        const baselineRun = runs.find((run) => run.scenario_name.endsWith("_BASELINE"))
        const scenarioRuns = runs.filter((run) => !run.scenario_name.endsWith("_BASELINE"))
        const sortedRuns = baselineRun ? [baselineRun, ...scenarioRuns] : scenarioRuns

        return (
          <RunTable
            sessionStorageName={`scenario-runs-${row.original.key}`}
            runs={sortedRuns.map((v) => ({
              ...v,
              base_graph_key: row.original.base_graph_key,
              is_creator_and_private:
                runGroups[0]?.creator_email === user?.email &&
                runGroups[0]?.owner_group_key === "private",
              family: row.original.family,
            }))}
            onSelectChange={handleSelectionChange}
            runGroup={row.original.key}
          />
        )
      }}
      muiTableBodyCellProps={({ row }) => {
        const onClick: TableCellProps["onClick"] = (event) => {
          const isInsideActionsCell = (event.target as HTMLElement).closest(".actions-cell")
          if (isInsideActionsCell) return

          setTableState((previousState) => {
            const expanded = {
              ...(typeof previousState.expanded === "object" ? previousState.expanded : {}),
            }
            if (row.id in expanded) {
              delete expanded[row.id]
            } else {
              expanded[row.id] = true
            }
            return {
              ...previousState,
              expanded,
            }
          })
        }
        return { onClick }
      }}
      muiTableBodyRowProps={({ row }) => {
        if (row.original.key !== runGroupToHighlightKey)
          return {
            sx: { transition: "background-color 5000ms linear" },
          }
        return {
          sx: {
            backgroundColor: "rgba(33, 150, 243, 0.2) !important",
            transition: "background-color 5000ms linear",
          },
        }
      }}
      muiTablePaperProps={{ sx: { overflow: "scroll" } }}
      renderTopToolbarCustomActions={() => (
        <Box sx={{ display: "inline" }}>
          <Tooltip
            title={
              isCompareButtonDisabled
                ? "To start comparison you need to select between 2 and 10 runs"
                : ""
            }
          >
            <Box sx={{ display: "inline" }}>
              <Button
                variant="contained"
                disabled={isCompareButtonDisabled}
                onClick={handleCompare}
              >
                Compare
              </Button>
            </Box>
          </Tooltip>
          <Button
            variant="contained"
            disabled={Object.keys(tableState.expanded).length === 0}
            onClick={handleCollapseAll}
            sx={{ ml: 1 }}
          >
            Collapse All
          </Button>
        </Box>
      )}
      enableRowActions
      renderRowActions={({ row }) => (
        <Box className="actions-cell">
          <RequestPublishingButton runGroup={row.original} onClick={handleRequestPublishing} />
        </Box>
      )}
    />
  )
}

export default RunGroupTable
